import React, { useEffect, useRef, useState } from 'react';
import { HERO_SLIDES } from '@/data/mock';
import { ChevronRight, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 7500);
    return () => clearInterval(timerRef.current);
  }, []);

  const slide = HERO_SLIDES[index];

  return (
    <section
      className="relative overflow-hidden min-h-[92vh] flex items-end"
    >
      {/* Media layers */}
      {HERO_SLIDES.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <video
            key={`${s.id}-${i === index}`}
            className={`w-full h-full object-cover ${i === index ? 'kenburns' : ''}`}
            src={s.video}
            poster={s.poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
          <div className="absolute inset-0 bg-[#07090c]/55 md:bg-[#07090c]/40" />
          <div className="absolute inset-0 hero-vignette" />
        </div>
      ))}

      <div className="absolute inset-0 viso-grid opacity-40 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full">
        <div className="max-w-7xl mx-auto px-6 pb-16 pt-24 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-300 mb-4">
              <span className="dot-pulse" /> {slide.kicker}
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.02]">
              <span className="text-gradient">{slide.title}</span>
              {slide.titleAccent && (
                <span className="text-gradient-accent">{slide.titleAccent}</span>
              )}
            </h1>
            <p className="mt-5 text-slate-300 max-w-2xl text-base md:text-lg">{slide.subtitle}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
                  Book a demo <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/platform">
                <Button variant="outline" className="border-white/15 bg-white/5 text-slate-100 hover:bg-white/10">
                  <Play className="w-4 h-4 mr-2 text-cyan-300" /> Explore the Platform
                </Button>
              </Link>
            </div>
          </div>

          <div className="md:col-span-4">
            <div className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-md p-5">
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Live · {slide.kicker}</div>
              <div className="space-y-3">
                {slide.metrics.map((m) => (
                  <div key={m.label} className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">{m.label}</span>
                    <span className="font-display font-semibold text-cyan-300">{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Slider controls */}
        <div className="max-w-7xl mx-auto px-6 pb-6">
          <div className="flex items-center gap-3">
            {HERO_SLIDES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIndex(i)}
                className={`group flex-1 text-left rounded-md transition-all ${i === index ? 'bg-white/5' : ''}`}
                aria-label={`Go to slide ${i + 1}`}
              >
                <div className={`h-[3px] rounded-full overflow-hidden bg-white/10`}>
                  <div className={`h-full bg-cyan-300 transition-all duration-[7500ms] ${i === index ? 'w-full' : 'w-0'}`} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
